import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useLanguageContext } from '../../../contexts/LanguageProvider';
import useLanguageActions from '../../../hooks/APIHooks/users/useLanguageActions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';

const LanguageModalContainer = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedLanguage,
    showDeleteModal,
    setTargetedLanguage,
    setSearchText,
    setShowDeleteModal,
  } = useLanguageContext();

  // integration of custom API hooks here
  const { deleteLanguageByIdHandler } = useLanguageActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete language setup declared here
  const deleteLanguageHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteLanguageByIdHandler
        .mutateAsync({ langId: targetedLanguage?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Language Setup Deleted!!');

            setShowDeleteModal(false);
            setSearchText('');
            setTargetedLanguage(null);
            queryClient.invalidateQueries({ queryKey: ['added-languages'] });
          } else {
            toast.error(res.data.message || 'Language Setup Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering language modal container component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this language (
                <span className='font-medium text-base-light-blue'>
                  {targetedLanguage?.name}
                </span>
                ) setup?
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteLanguageHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default LanguageModalContainer;
